/**
 * Google Consent Mode V2 + GDPR helpers.
 *
 * Consent Mode V2 (mandatory since March 2024 for EEA traffic) requires the
 * site to set 7 consent signals — including the two new ones, ad_user_data
 * and ad_personalization — BEFORE any Google tag fires. Defaults are set in
 * `client/index.html` so they run before main.tsx; this module owns the
 * `update` step that runs after the user makes a choice.
 *
 * Mapping of the 4 user-facing categories to the 7 Google signals:
 *
 *   necessary      → security_storage           (always granted)
 *   preferences    → functionality_storage,
 *                    personalization_storage
 *   analytics      → analytics_storage
 *   marketing      → ad_storage,
 *                    ad_user_data,
 *                    ad_personalization
 *
 * Persistence: a versioned record in localStorage with a 12-month TTL (the
 * upper limit recommended by the Garante / EDPB for renewing consent).
 */

export type ConsentCategory =
  | "necessary"
  | "preferences"
  | "analytics"
  | "marketing";

export interface CookiePreferences {
  necessary: true;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface StoredConsent {
  version: number;
  timestamp: number;
  prefs: CookiePreferences;
}

export const CONSENT_STORAGE_KEY = "ab-mark-consent";
export const CONSENT_VERSION = 2;
export const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

export const DEFAULT_DENIED_PREFS: CookiePreferences = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

export const ALL_GRANTED_PREFS: CookiePreferences = {
  necessary: true,
  preferences: true,
  analytics: true,
  marketing: true,
};

type ConsentState = "granted" | "denied";

interface ConsentSignals {
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
  analytics_storage: ConsentState;
  functionality_storage: ConsentState;
  personalization_storage: ConsentState;
  security_storage: ConsentState;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Translate the 4 user-facing toggles into the 7 Google Consent Mode V2
 * signals. `security_storage` is always granted (strictly necessary).
 */
export function prefsToSignals(prefs: CookiePreferences): ConsentSignals {
  const grant = (b: boolean): ConsentState => (b ? "granted" : "denied");
  return {
    security_storage: "granted",
    functionality_storage: grant(prefs.preferences),
    personalization_storage: grant(prefs.preferences),
    analytics_storage: grant(prefs.analytics),
    ad_storage: grant(prefs.marketing),
    ad_user_data: grant(prefs.marketing),
    ad_personalization: grant(prefs.marketing),
  };
}

/**
 * Push a `consent: update` to gtag/dataLayer after the user makes a choice.
 * Safe to call on every save — gtag handles repeated updates idempotently.
 *
 * Also emits a `consent_update` event so a GTM container can react to
 * preference changes without polling.
 */
export function pushConsentUpdate(prefs: CookiePreferences): void {
  if (typeof window === "undefined") return;
  const signals = prefsToSignals(prefs);

  // Initialise dataLayer + gtag stub if the inline snippet in index.html
  // hasn't run yet (e.g. unit tests, SSR hydration edge cases).
  if (!window.dataLayer) window.dataLayer = [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtagShim(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }

  window.gtag("consent", "update", signals);
  window.dataLayer.push({
    event: "consent_update",
    consent_categories: {
      necessary: prefs.necessary,
      preferences: prefs.preferences,
      analytics: prefs.analytics,
      marketing: prefs.marketing,
    },
  });
}

/* ------------------------------------------------------------------ *
 * Storage helpers
 * ------------------------------------------------------------------ */

function isCookiePrefs(v: unknown): v is CookiePreferences {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.analytics === "boolean" &&
    typeof o.marketing === "boolean"
  );
}

/**
 * Read consent from localStorage. Returns `null` if no record exists, the
 * record is malformed, or the record is older than CONSENT_TTL_MS — in all
 * three cases the caller should re-prompt.
 *
 * Backward-compatible with the original v1 record (3 categories, no
 * timestamp): we accept it and migrate on the next save.
 */
export function readStoredConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;

    // v2 shape: { version, timestamp, prefs }
    if (
      parsed &&
      typeof parsed === "object" &&
      "prefs" in parsed &&
      "timestamp" in parsed
    ) {
      const rec = parsed as Partial<StoredConsent>;
      if (
        typeof rec.timestamp === "number" &&
        isCookiePrefs(rec.prefs)
      ) {
        const ageMs = Date.now() - rec.timestamp;
        if (ageMs > CONSENT_TTL_MS) return null;
        return {
          version: rec.version ?? CONSENT_VERSION,
          timestamp: rec.timestamp,
          prefs: {
            necessary: true,
            preferences: rec.prefs.preferences ?? false,
            analytics: rec.prefs.analytics,
            marketing: rec.prefs.marketing,
          },
        };
      }
    }

    // v1 shape: { necessary, analytics, marketing } (no timestamp)
    if (isCookiePrefs(parsed)) {
      return {
        version: 1,
        timestamp: Date.now(),
        prefs: {
          necessary: true,
          preferences: false,
          analytics: parsed.analytics,
          marketing: parsed.marketing,
        },
      };
    }
  } catch {
    /* fall through */
  }
  return null;
}

export function writeStoredConsent(prefs: CookiePreferences): StoredConsent {
  const record: StoredConsent = {
    version: CONSENT_VERSION,
    timestamp: Date.now(),
    prefs: { ...prefs, necessary: true },
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch {
      /* quota / private mode — non-fatal */
    }
  }
  return record;
}

export function clearStoredConsent(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}
